package com.sddamico.draftmachine.util

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiGet
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.sddamico.draftmachine.models.LogoutRequest
import com.sddamico.draftmachine.models.MeResponse
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.models.UpdateUsernameRequest

/**
 * The current session, sourced from `/api/auth/me`. A [LoadState.Ready] with a `null` value
 * means signed out — a 401 is treated as "no session", not a failure.
 */
class AuthState {
  var value: LoadState<MeResponse?> by mutableStateOf(LoadState.Loading)
    internal set

  /**
   * Posts `/api/auth/logout` (ignoring any error — the session is cleared client-side either way)
   * and un-enrolls this browser from push, so alerts for the account stop arriving here.
   */
  suspend fun logout() {
    val pushEndpoint = currentPushEndpoint()
    try {
      apiPost(
        "/api/auth/logout",
        apiJson.encodeToString(LogoutRequest.serializer(), LogoutRequest(pushEndpoint)),
        SuccessResponse.serializer(),
      )
      // Only once the server has let go of it: if the sign-out did not land, the session is still
      // live and should keep its alerts.
      releasePushSubscription()
    } catch (_: Throwable) {
    }
    value = LoadState.Ready(null)
  }

  suspend fun updateUsername(username: String): MeResponse {
    val updated = apiPost(
      "/api/auth/me",
      apiJson.encodeToString(
        UpdateUsernameRequest.serializer(),
        UpdateUsernameRequest(username),
      ),
      MeResponse.serializer(),
    )
    value = LoadState.Ready(updated)
    return updated
  }
}

/** Fetches the session once on first composition and exposes it as an [AuthState]. */
@Composable
fun rememberAuthState(): AuthState {
  val auth = remember { AuthState() }
  // The page reacts to auth.value below, so that is where the load settles visibly.
  val fetched = rememberApiResource<MeResponse?>(settleMotion = null) {
    try {
      apiGet("/api/auth/me", MeResponse.serializer())
    } catch (e: ApiException) {
      if (e.status == 401) null else throw e
    }
  }
  // fetched settles once (Loading -> Ready/Failed) and stays put for the rest of this
  // composition, so keying on it here only ever seeds auth.value — it can't re-fire and
  // clobber a later logout() write.
  LaunchedEffect(fetched) {
    if (fetched !is LoadState.Loading) ViewTransitions.run(Motion.Fade) { auth.value = fetched }
  }
  return auth
}
